import { useMemo, useState, useEffect, type FormEvent } from 'react'
import { Alert, Button, CircularProgress, Divider, Stack, TextField, Typography } from '@mui/material'
import { Navigate, useLocation, useSearchParams, type Location } from 'react-router-dom'
import { FcGoogle } from 'react-icons/fc'

import useAuth from '../models/useAuth'
import { getOrigin } from '../lib/origin'
import { workosOrganizationId } from '../lib/auth'
import { inNativeApp } from '../lib/nativeApp'
import { routerLoginURL } from '../utilities/routerLogin'
import { rememberedEmail } from '../lib/rememberedEmail'
import { signInErrorMessage } from '../utilities/signInError'
import { safeNextPath } from '../utilities/authCallback'
import {
  clearSignedOut,
  lastSilentAttempt,
  markSilentAttempt,
  shouldTrySilentSignIn,
  signedOutInThisTab,
} from '../utilities/silentSignIn'

// ONE screen, both email methods first class: an emailed code is the main
// button, and a password sits beside it. The router signs the visitor in
// (router auth/email_signin.go), so AuthKit's hosted page is only the path for
// what this screen does not do: Google goes straight to Google, and a password
// sign-up or reset, MFA and SSO continue on the hosted page. A code also signs a
// new address up, so "Need an account?" is the same box; the sign-up button
// stays for a person who wants a password (app#116).
const routerApiBaseUrl =
  import.meta.env.VITE_ROUTER_API_URL?.replace(/\/$/, '') ?? 'https://router.nobgp.com'

// ⚠ The silent sign-in (utilities/silentSignIn.ts) sends a visitor with no
// session to AuthKit's page, which would hide this screen from every new web
// visitor. Inside the noBGP app no other noBGP site can hold a sign-in, so the
// app never bounces. On the web it still does (Benoit, 2026-09-21).
const SILENT_SIGN_IN = !inNativeApp

// The address and destination of a sign-in in flight, so a refused code or
// password comes back to the same screen with the address filled in, and the
// next try still lands on the invitation or page that sent the visitor here.
// Per tab, and only until the next try.
const PENDING_KEY = 'nobgp.signin.pending'

type Pending = { email: string; codeSent: boolean; next: string }

const readPending = (): Pending | null => {
  try {
    const v = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? 'null')
    return typeof v?.email === 'string'
      ? { email: v.email, codeSent: v.codeSent === true, next: safeNextPath(typeof v.next === 'string' ? v.next : null) }
      : null
  } catch {
    return null
  }
}

const writePending = (p: Pending | null) => {
  try {
    if (p) sessionStorage.setItem(PENDING_KEY, JSON.stringify(p))
    else sessionStorage.removeItem(PENDING_KEY)
  } catch {
    // only a prefill
  }
}

// The theme lays a button's label out from the start; these are centred.
const centered = { justifyContent: 'center', gap: 1 }

const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())

export default function SignIn() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const session = useAuth(state => state.session)
  const initialized = useAuth(state => state.initialized)

  const error = searchParams.get('error')
  const pending = useMemo(() => (error ? readPending() : null), [error])

  const [email, setEmail] = useState(() => pending?.email ?? rememberedEmail() ?? '')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [codeSent, setCodeSent] = useState(pending?.codeSent === true && error === 'invalid_code')
  // WHICH action is in flight, so only its own button says so.
  const [busy, setBusy] = useState<'code' | 'verify' | 'password' | 'google' | 'up' | 'hosted' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Whether the router serves this screen's code and password (auth/email_signin.go). ⚠ A router
  // before it (prod v0.4.212 on 2026-11-01) answers 404 with no CORS headers, so the fetch THROWS.
  // An empty request is a 400 and sends nothing. Without the door, both go to AuthKit's page.
  const [emailDoor, setEmailDoor] = useState<boolean | null>(null)
  useEffect(() => {
    fetch(`${routerApiBaseUrl}/auth/email/code`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    }).then(
      res => setEmailDoor(res.status !== 404),
      () => setEmailDoor(false)
    )
  }, [])

  const errorMessage = notice ?? signInErrorMessage(error, searchParams.get('error_description'))

  const from = (location.state as { from?: Location } | null)?.from
  const nextPath = from ? `${from.pathname}${from.search}` : (pending?.next ?? '/')

  const returnTo = useMemo(() => {
    const origin = getOrigin()

    if (!origin) return '/auth/callback'

    const searchParams = new URLSearchParams()
    searchParams.set('next', nextPath)
    return `${origin}/auth/callback?${searchParams.toString()}`
  }, [nextPath])

  // Google, a password sign-up, a reset: the router's login, then AuthKit or Google.
  // Inside the noBGP app this navigation opens the system sign-in sheet (mobile/).
  const goToRouterLogin = (door: 'google' | 'up' | 'hosted' | 'silent') => {
    clearSignedOut()
    if (door !== 'silent') setBusy(door)
    window.location.assign(
      routerLoginURL(routerApiBaseUrl, {
        returnTo,
        organizationId: workosOrganizationId || undefined,
        // Prefills AuthKit's email box: only for an address typed here or one
        // remembered from an email sign-in (rememberedEmail.ts), never on sign-up.
        loginHint: door === 'hosted' || door === 'silent' ? email.trim() || undefined : undefined,
        signUp: door === 'up',
        google: door === 'google',
      }),
    )
  }

  // The router's form door: a navigation, so the router sets its cookies and
  // sends the app its tokens exactly as after AuthKit.
  const submitToRouter = (fields: Record<string, string>) => {
    writePending({ email: email.trim(), codeSent, next: nextPath })
    clearSignedOut()
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `${routerApiBaseUrl}/auth/email/signin`
    const scope = workosOrganizationId ? { organization_id: workosOrganizationId } : {}
    for (const [name, value] of Object.entries({ ...fields, ...scope, email: email.trim(), returnTo })) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    }
    document.body.appendChild(form)
    form.submit()
  }

  const sendCode = async () => {
    if (!isEmail(email)) {
      setNotice('Enter your email address.')
      return
    }
    setNotice(null)
    if (emailDoor === false) {
      goToRouterLogin('hosted')
      return
    }
    setBusy('code')
    try {
      const res = await fetch(`${routerApiBaseUrl}/auth/email/code`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
      if (res.ok) {
        setCodeSent(true)
        setCode('')
      } else {
        setNotice(
          res.status === 429 ? 'Too many codes asked for. Wait a few minutes, then try again.' : 'The code could not be sent. Try again.',
        )
      }
    } catch {
      setNotice('The code could not be sent. Check your connection and try again.')
    } finally {
      setBusy(null)
    }
  }

  const onCode = (e: FormEvent) => {
    e.preventDefault()
    if (!codeSent) {
      void sendCode()
      return
    }
    if (!code.trim()) {
      setNotice('Enter the code from the email.')
      return
    }
    setBusy('verify')
    submitToRouter({ code: code.trim() })
  }

  const onPassword = (e: FormEvent) => {
    e.preventDefault()
    if (!isEmail(email) || !password) {
      setNotice('Enter your email address and your password.')
      return
    }
    if (emailDoor === false) {
      goToRouterLogin('hosted')
      return
    }
    setBusy('password')
    submitToRouter({ password })
  }

  // Signed in elsewhere (another noBGP service): reach the app with no click.
  // See utilities/silentSignIn.ts for the three cases that must not bounce.
  useEffect(() => {
    const now = Date.now()
    if (
      SILENT_SIGN_IN &&
      !busy &&
      shouldTrySilentSignIn({
        initialized,
        hasSession: session !== null,
        hasError: error !== null,
        signedOutInThisTab: signedOutInThisTab(),
        lastAttemptAt: lastSilentAttempt(),
        now,
      })
    ) {
      markSilentAttempt(now)
      goToRouterLogin('silent')
    }
    // Runs when the session check settles; a later visit decides again.
  }, [initialized, session])

  if (initialized && session) {
    writePending(null)
    return <Navigate to={nextPath} replace />
  }

  // On the web a visitor with no session goes to AuthKit when the session check
  // settles (the effect above). Until then show no form: text typed in it would
  // be lost to that navigation.
  if (
    !initialized &&
    SILENT_SIGN_IN &&
    shouldTrySilentSignIn({
      initialized: true,
      hasSession: false,
      hasError: error !== null,
      signedOutInThisTab: signedOutInThisTab(),
      lastAttemptAt: lastSilentAttempt(),
      now: Date.now(),
    })
  ) {
    return (
      <Stack sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '95vh' }}>
        <CircularProgress />
      </Stack>
    )
  }

  const spin = (label: string) => (
    <>
      {label}
      <CircularProgress color="inherit" size={20} />
    </>
  )

  return (
    <Stack
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '95vh',
        padding: 4
      }}>
      <Stack sx={{ width: '100%', maxWidth: 360, gap: 2 }}>
        {errorMessage && (
          <Alert severity="error" variant="filled">
            {errorMessage}
          </Alert>
        )}

        <Stack component="form" onSubmit={onCode} noValidate sx={{ gap: 2 }}>
          <TextField
            label="Email"
            type="email"
            autoComplete="username email"
            value={email}
            onChange={e => {
              setEmail(e.target.value)
              if (codeSent) setCodeSent(false)
            }}
            disabled={busy !== null}
            fullWidth
          />
          {codeSent && (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                We sent a code to {email.trim()}. It works for 10 minutes.
              </Typography>
              <TextField
                label="Code"
                autoComplete="one-time-code"
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                value={code}
                onChange={e => setCode(e.target.value)}
                disabled={busy !== null}
                autoFocus
                fullWidth
              />
            </>
          )}
          <Button type="submit" size="large" variant="contained" color="secondary" disabled={busy !== null} sx={centered}>
            {busy === 'code'
              ? spin('Sending…')
              : busy === 'verify'
                ? spin('Signing in…')
                : codeSent
                  ? 'Sign in with code'
                  : 'Email me a sign-in code'}
          </Button>
          {codeSent && (
            <Button variant="text" size="small" onClick={() => void sendCode()} disabled={busy !== null}>
              Send a new code
            </Button>
          )}
        </Stack>

        <Divider>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            or with your password
          </Typography>
        </Divider>

        <Stack component="form" onSubmit={onPassword} noValidate sx={{ gap: 2 }}>
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={busy !== null}
            fullWidth
          />
          <Button type="submit" size="large" variant="outlined" color="secondary" disabled={busy !== null} sx={centered}>
            {busy === 'password' ? spin('Signing in…') : 'Sign in with password'}
          </Button>
          <Button variant="text" size="small" onClick={() => goToRouterLogin('hosted')} disabled={busy !== null}>
            {busy === 'hosted' ? 'Opening…' : 'Forgot your password?'}
          </Button>
        </Stack>

        <Divider>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            or
          </Typography>
        </Divider>

        <Button
          size="large"
          variant="outlined"
          color="inherit"
          startIcon={<FcGoogle />}
          onClick={() => goToRouterLogin('google')}
          disabled={busy !== null}
          sx={centered}
        >
          {busy === 'google' ? spin('Opening Google…') : 'Continue with Google'}
        </Button>

        <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            New here? A code creates your account.
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => goToRouterLogin('up')}
            disabled={busy !== null}
            sx={{ paddingX: 1 }}
          >
            {busy === 'up' ? 'Opening…' : 'Sign up with a password'}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}
